/*
 * Author: Harper Yan
 * File Created: Friday, 24-07-19 02:32 pm
 * Last Modified: Thursday, 25-04-24 05:47 pm, by Dw
 */

import { useCallback, useEffect, useState } from 'react';

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { cookies } from '@web/utils';

import type { UserDetail } from '@/@types/account';
import type { KycStatusInfo } from '@/@types/kyc';
import { getUserDetail, userLogout } from '@/services/account/service';
import { getKycStatus } from '@/services/kyc/service';
import logger from '@/utils/logger';

interface UserStoreState {
  user?: UserDetail;
  kycStatus?: KycStatusInfo;
  kycFull?: boolean;
  isLogin?: boolean;
}

interface UserStoreAction {
  setLogin: (_v: boolean) => void;
  getUserInfo: () => Promise<void>;
  getKycStatusInfo: (_full: boolean) => Promise<void>;
  logout: () => Promise<boolean>;
}

export const useUserStore = create<UserStoreState & UserStoreAction>((set) => ({
  logout: async () => {
    try {
      const { data } = await userLogout({});

      if (data) {
        set(() => ({
          user: undefined,
          isLogin: false,
          kycStatus: undefined,
          kycFull: false,
        }));

        return true;
      }
    } catch (error) {
      return false;
    }

    return false;
  },
  setLogin: (data: boolean) => {
    set(() => ({ isLogin: data }));
  },
  getUserInfo: async () => {
    const { data: user } = await getUserDetail();

    set(() => ({ user, isLogin: true }));
  },
  getKycStatusInfo: async (full = false) => {
    const { data } = await getKycStatus({ queryBasicInfo: full });

    if (full) {
      set(() => ({ kycFull: true }));
    }

    set(() => ({ kycStatus: data }));
  },
}));

const loadingRef = {
  isUserLoading: false,
  isKycStatusLoading: false,
};

export const useUserInfo = () => {
  const { user, getUserInfo, isLogin, setLogin } = useUserStore(
    useShallow((state) => ({
      user: state.user,
      isLogin: state.isLogin,
      getUserInfo: state.getUserInfo,
      setLogin: state.setLogin,
    })),
  );

  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchUserInfo = useCallback(async () => {
    if (loadingRef.isUserLoading) {
      return;
    }

    const login = cookies.get('x-login') === 'Y';

    setLogin(login);

    if (login) {
      try {
        loadingRef.isUserLoading = true;
        setLoading(true);
        await getUserInfo();
      } catch (error) {
        logger.error('Fail to get user information: ', error);
      }

      setFetched(true);
      loadingRef.isUserLoading = false;
      setLoading(false);
    }
  }, [getUserInfo, setLogin]);

  useEffect(() => {
    if (!fetched && !loading && !user) {
      void fetchUserInfo();
    }
  }, [getUserInfo, fetched, loading, fetchUserInfo, user]);

  return { user, loading, isLogin, fetchUserInfo };
};

export const useKycStatusInfo = (full = false) => {
  const { kycStatus, kycFull, getKycStatusInfo, isLogin } = useUserStore(
    useShallow((state) => ({
      kycStatus: state.kycStatus,
      kycFull: state.kycFull,
      getKycStatusInfo: state.getKycStatusInfo,
      isLogin: state.isLogin,
    })),
  );

  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const [retryCount, setRetryCount] = useState(0); // Add a retry counter

  const fetchKyc = useCallback(
    async (isFull: boolean) => {
      if (loadingRef.isKycStatusLoading) {
        return;
      }

      try {
        loadingRef.isKycStatusLoading = true;
        setLoading(true);

        await getKycStatusInfo(isFull);
      } catch (error) {
        logger.error('Fail to get KycStatus: ', error);
      }

      setFetched(true);
      loadingRef.isKycStatusLoading = false;
      setLoading(false);
    },
    [getKycStatusInfo],
  );

  useEffect(() => {
    if (!isLogin || loading) {
      // is fetching
      return;
    }

    if ((!kycStatus && !fetched) || (!kycFull && full)) {
      // kycStatus not fetched or only partially fetch
      void fetchKyc(full);
    }
  }, [fetchKyc, fetched, loading, kycStatus, kycFull, full, isLogin]);

  const loopKycStatusWhenPending = useCallback(() => {
    if (!kycStatus) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- ignore
    const interval = setInterval(async () => {
      const mergedCardStatusType =
        kycStatus.status === 'PENDING'
          ? kycStatus.cardApplyStatus
          : kycStatus.status;

      if (!mergedCardStatusType) {
        return;
      }

      if (retryCount >= 10) {
        clearInterval(interval);

        return;
      }

      if (['INIT', 'PASS', 'REFUSE', 'UNDO'].includes(mergedCardStatusType)) {
        // 这里如果是pending 不应该2秒后再调用一次吧
        // await fetchUserInfo();
        clearInterval(interval);
      } else {
        await fetchKyc(false);
        setRetryCount((prevCount) => prevCount + 1);
      }
    }, 2000); // Poll every 2 seconds

    // Clear the interval when component is unmounted
    return () => {
      clearInterval(interval);
    };
  }, [fetchKyc, kycStatus, retryCount]);

  return { kycStatus, loading, fetchKyc, loopKycStatusWhenPending };
};
